import { useState, useContext, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../services/apiClient";
import {
    Card,
    Button,
    Input,
    Switch,
    message,
    Typography,
    Divider,
    Space,
    Modal,
    List,
    Spin,
    Select,
    Tabs,
} from "antd";
import SubscriptionDetails from "./Profile/SubscriptionDetails";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    LockOutlined,
    SafetyOutlined,
    LogoutOutlined,
    EditOutlined,
    CheckOutlined,
    UploadOutlined,
    DeleteOutlined,
    BulbOutlined,
    CalendarOutlined,
    QrcodeOutlined,
    WindowsOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import { useUser } from "../context/UserContext";
import { clearAuthData, isAdmin } from "../utils/roleUtils";
import "./Profile.css";

const { Title, Text } = Typography;

const Profile = () => {
    const { isDark, toggleTheme } = useContext(ThemeContext);
    const { userData, updateUserData, loading, clearUserData } = useUser();
    const navigate = useNavigate();
    const [editMode, setEditMode] = useState(false);
    const [localLoading, setLocalLoading] = useState(false);
    const [notifEnabled, setNotifEnabled] = useState(true);
    const [deleteModal, setDeleteModal] = useState(false);
    const [twoFAModal, setTwoFAModal] = useState(false);
    const [availableMethods, setAvailableMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [twoFALoading, setTwoFALoading] = useState(false);
    const [enable2FAModal, setEnable2FAModal] = useState(false);
    const [enable2FAMethod, setEnable2FAMethod] = useState(null);
    const [enable2FACode, setEnable2FACode] = useState("");
    const [enable2FALoading, setEnable2FALoading] = useState(false);
    const fileInput = useRef();

    // Form state for editing
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        mobileNo: "",
        age: null,
        dob: null,
    });

    // Initialize form data when userData changes
    useEffect(() => {
        console.log("Profile - userData changed:", userData);
        if (userData && userData.id) {
            console.log("Profile - Setting form data from userData");
            setFormData({
                firstName: userData.firstName || "",
                lastName: userData.lastName || "",
                mobileNo: userData.mobileNo || "",
                age: userData.age || null,
                dob: userData.dob || null,
            });
        } else {
            console.log("Profile - userData is empty or missing id");
        }
    }, [userData]);

    // Fetch 2FA methods once user id is available
    useEffect(() => {
        if (userData?.id) {
            fetchAvailableMethods();
        }
    }, [userData?.id]);

    // Update user profile
    const updateUserProfile = async () => {
        setLocalLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_AUTH_URL || "";

            // Prepare the payload with all available fields
            const payload = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                mobileNo: formData.mobileNo,
                age: formData.age,
                dob: formData.dob, // Keep as string (YYYY-MM-DD format)
                profile: userData.profile, // This will be the base64 image if updated
            };

            const response = await apiClient.put(
                `${baseUrl}/user/update/${userData.id}`,
                payload
            );

            if (response.data.success) {
                message.success("Profile updated successfully!");
                setEditMode(false);

                // Update the user data in context with the new values
                const updatedUserData = {
                    ...userData,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    mobileNo: formData.mobileNo,
                    age: formData.age,
                    dob: formData.dob,
                };
                updateUserData(updatedUserData);
            } else {
                message.error(response.data.message || "Failed to update profile");
            }
        } catch (error) {
            console.error("Error updating profile:", error);
            message.error("Failed to update profile");
        } finally {
            setLocalLoading(false);
        }
    };

    // Handle profile image upload with base64 conversion
    const handleProfileImageUpload = async (file) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const base64String = e.target.result;
                // Update the userData with the new profile image
                updateUserData({ ...userData, profile: base64String });

                // Automatically save the profile with the new image
                await saveProfileWithImage(base64String);
                message.success("Profile image updated successfully!");
            } catch (error) {
                message.error("Failed to update profile image");
                console.error("Error updating profile image:", error);
            }
        };
        reader.readAsDataURL(file);
    };

    // Save profile with image to backend
    const saveProfileWithImage = async (profileImage) => {
        const baseUrl = process.env.REACT_APP_AUTH_URL || "";

        const payload = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            mobileNo: formData.mobileNo,
            age: formData.age,
            dob: formData.dob,
            profile: profileImage, // The base64 image
        };

        const response = await apiClient.put(
            `${baseUrl}/user/update/${userData.id}`,
            payload
        );

        if (!response.data.success) {
            throw new Error(response.data.message || "Failed to update profile");
        }
    };

    // Handle avatar change with base64 conversion
    const handleAvatarChange = async (info) => {
        if (info.file.status === "done" || info.file.status === "uploading") {
            try {
                await handleProfileImageUpload(info.file.originFileObj);
                // Success message is handled in the upload function
            } catch (error) {
                message.error("Failed to update profile image");
                console.error("Error updating profile image:", error);
            }
        }
    };

    // Remove the useEffect that calls fetchUserDetails since UserContext handles it
    // useEffect(() => {
    //     fetchUserDetails();
    // }, []);

    // Generate initials from name
    const getInitials = () => {
        const firstName = userData.firstName || "";
        const lastName = userData.lastName || "";
        const name = `${firstName} ${lastName}`.trim();

        if (name) {
            return name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase();
        }

        // Fallback to email initials
        return userData.email
            ? userData.email.substring(0, 2).toUpperCase()
            : "U";
    };

    // Get display name
    const getDisplayName = () => {
        const firstName = userData.firstName || "";
        const lastName = userData.lastName || "";
        const name = `${firstName} ${lastName}`.trim();

        return name || userData.email || "User";
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Format date time
    const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleSave = () => {
        updateUserProfile();
    };

    const handleLogout = () => {
        clearAuthData(navigate, clearUserData);
    };

    const handleChangePassword = () => {
        message.info("Change password flow (not implemented)");
    };

    // Get method icon
    const getMethodIcon = (methodCode) => {
        switch (methodCode) {
            case 'google':
                return <QrcodeOutlined style={{ color: "#4285F4" }} />;
            case 'microsoft':
                return <WindowsOutlined style={{ color: "#00A4EF" }} />;
            case 'email':
                return <MailOutlined style={{ color: "#1890ff" }} />;
            case 'phone':
                return <PhoneOutlined style={{ color: "#52c41a" }} />;
            default:
                return <SafetyOutlined />;
        }
    };

    // Fetch available 2FA methods and their status
    const fetchAvailableMethods = async () => {
        try {
            const baseUrl = process.env.REACT_APP_AUTH_URL || "";

            // Fetch both available methods and their status
            const [methodsResponse, statusResponse] = await Promise.all([
                apiClient.get(`${baseUrl}/2fa/methods`),
                apiClient.get(`${baseUrl}/2fa/status`)
            ]);

            if (methodsResponse.data.success && statusResponse.data.success) {
                const availableMethods = methodsResponse.data.data || [];
                const statusMethods = statusResponse.data.data.methods || [];

                // Create a map of enabled methods from status API
                const enabledMethodsMap = {};
                statusMethods.forEach(method => {
                    enabledMethodsMap[method.methodCode] = {
                        enabled: method.enabled,
                        setupCompleted: method.setupCompleted,
                        phoneNumber: method.phoneNumber
                    };
                });

                // Merge the data: available methods with actual enabled status
                const mergedMethods = availableMethods.map(method => {
                    const status = enabledMethodsMap[method.code] || { enabled: false, setupCompleted: false };
                    return {
                        ...method,
                        methodCode: method.code, // Keep both for compatibility
                        methodName: method.name, // Keep both for compatibility
                        enabled: status.enabled,
                        setupCompleted: status.setupCompleted,
                        phoneNumber: status.phoneNumber,
                        enabledAt: status.enabled ? new Date().toISOString() : null // Fallback date
                    };
                });

                setAvailableMethods(mergedMethods);
            } else {
                message.error("Failed to fetch 2FA methods");
            }
        } catch (error) {
            console.error("Error fetching 2FA methods:", error);
            message.error("Failed to fetch 2FA methods");
        }
    };



    // Disable specific 2FA method
    const handleDisableMethod = async (methodCode, methodName) => {
        // Show confirmation dialog first
        Modal.confirm({
            title: `Disable ${methodName}`,
            content: `Are you sure you want to disable ${methodName} 2FA? This will remove this authentication method from your account.`,
            okText: 'Disable',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setTwoFALoading(true);
                try {
                    const baseUrl = process.env.REACT_APP_AUTH_URL || "";

                    const response = await apiClient.post(`${baseUrl}/2fa/disable-2fa?methodCode=${methodCode}`);

                    if (response.data.success) {
                        message.success(`${methodName} 2FA disabled successfully`);
                        await fetchAvailableMethods(); // Refresh methods
                    } else {
                        message.error(response.data.message || "Failed to disable 2FA method");
                    }
                } catch (error) {
                    console.error("Error disabling 2FA method:", error);
                    message.error("Failed to disable 2FA method");
                } finally {
                    setTwoFALoading(false);
                }
            }
        });
    };

    // Disable all 2FA methods
    const handleDisableAll2FA = async () => {
        setTwoFALoading(true);
        try {
            const baseUrl = process.env.REACT_APP_AUTH_URL || "";

            const response = await apiClient.post(`${baseUrl}/2fa/disable-all-2fa`);

            if (response.data.success) {
                message.success("All 2FA methods disabled successfully");
                await fetchAvailableMethods(); // Refresh methods instead of clearing
            } else {
                message.error(response.data.message || "Failed to disable all 2FA methods");
            }
        } catch (error) {
            console.error("Error disabling all 2FA methods:", error);
            message.error("Failed to disable all 2FA methods");
        } finally {
            setTwoFALoading(false);
        }
    };

    const handleManage2FA = async () => {
        // First fetch current 2FA methods
        await fetchAvailableMethods();
        // Show the 2FA methods modal
        setTwoFAModal(true);
    };

    // Enable 2FA method
    const handleEnable2FA = async () => {
        if (!enable2FACode.trim()) {
            message.error("Please enter the verification code");
            return;
        }

        setEnable2FALoading(true);
        try {
            const baseUrl = process.env.REACT_APP_AUTH_URL || "";

            const response = await apiClient.post(`${baseUrl}/2fa/enable-2fa`, {
                method: enable2FAMethod,
                code: enable2FACode
            });

            if (response.data.success) {
                message.success("2FA method enabled successfully!");
                setEnable2FAModal(false);
                setEnable2FACode("");
                setEnable2FAMethod(null);
                await fetchAvailableMethods(); // Refresh methods
            } else {
                message.error(response.data.message || "Failed to enable 2FA method");
            }
        } catch (error) {
            console.error("Error enabling 2FA method:", error);
            message.error("Failed to enable 2FA method");
        } finally {
            setEnable2FALoading(false);
        }
    };

    const handleDeleteAccount = () => {
        setDeleteModal(false);
        message.success("Account deleted (mock)");
    };

    console.log("Profile - Component render - loading:", loading, "userData:", userData);

    if (loading && !userData?.id) {
        console.log("Profile - Showing loading spinner");
        return (
            <div
                className={`profile-container ${isDark ? "dark" : ""}`}
                style={{
                    minHeight: "100vh",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className={`profile-container ${isDark ? "dark" : ""}`}>
            {/* Header Section */}
            <div className="profile-header">
                <div className="profile-avatar-section">
                    <div className="profile-avatar" onClick={() => fileInput.current.click()}>
                        {userData.profile ? (
                            <img src={userData.profile} alt="Profile" />
                        ) : (
                            <div className="avatar-initials">
                                {getInitials()}
                            </div>
                        )}
                    </div>
                    <input
                        ref={fileInput}
                        type="file"
                        accept="image/*"
                        className="hidden-file-input"
                        onChange={async (e) => {
                            if (e.target.files && e.target.files[0]) {
                                try {
                                    await handleProfileImageUpload(e.target.files[0]);
                                } catch (error) {
                                    message.error("Failed to update profile image");
                                    console.error("Error updating profile image:", error);
                                }
                            }
                        }}
                    />
                </div>

                <div className="profile-info">
                    <h1 className="profile-email">{userData?.email || "user@simplyfi.co.in"}</h1>
                    <p className="profile-role">{(userData.roles || []).join(", ")}</p>
                    <div className="profile-status">
                        <span className={`status-badge ${userData.deactivated ? '' : 'active'}`}>
                            {userData.deactivated ? "Deactivated" : "Active"}
                        </span>
                    </div>
                </div>

                <div className="profile-dates">
                    <div className="date-item">
                        <span className="date-label">Joined</span>
                        <span className="date-value">{formatDate(userData.createdAt)}</span>
                    </div>
                    <div className="date-item">
                        <span className="date-label">Last updated</span>
                        <span className="date-value">{formatDate(userData.updatedAt)}</span>
                    </div>
                </div>
            </div>

            {/* Cards Section */}
            <div className="profile-cards">
                {/* Personal Info */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3>Personal Info</h3>
                        <button className="edit-btn" onClick={() => setEditMode(!editMode)}>
                            Edit
                        </button>
                    </div>
                    <div className="card-content">
                        {editMode ? (
                            <Space direction="vertical" size={20} style={{ width: "100%" }}>
                                <div>
                                    <Text type="secondary" style={{ fontWeight: 500 }}>
                                        First Name
                                    </Text>
                                    <Input
                                        value={formData.firstName}
                                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                                        style={{ marginTop: 4, fontWeight: 600 }}
                                        maxLength={32}
                                        placeholder="Enter first name"
                                    />
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontWeight: 500 }}>
                                        Last Name
                                    </Text>
                                    <Input
                                        value={formData.lastName}
                                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                                        style={{ marginTop: 4, fontWeight: 600 }}
                                        maxLength={32}
                                        placeholder="Enter last name"
                                    />
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontWeight: 500 }}>
                                        Phone
                                    </Text>
                                    <Input
                                        value={formData.mobileNo}
                                        onChange={(e) => setFormData(prev => ({ ...prev, mobileNo: e.target.value }))}
                                        style={{ marginTop: 4, fontWeight: 600 }}
                                        maxLength={16}
                                        placeholder="Enter phone number"
                                    />
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontWeight: 500 }}>
                                        Date of Birth
                                    </Text>
                                    <Input
                                        type="date"
                                        value={formData.dob ? (typeof formData.dob === 'string' ? formData.dob : formData.dob.toISOString().slice(0, 10)) : ""}
                                        onChange={(e) => setFormData(prev => ({ ...prev, dob: e.target.value || null }))}
                                        style={{ marginTop: 4, fontWeight: 600 }}
                                    />
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontWeight: 500 }}>
                                        Age
                                    </Text>
                                    <Input
                                        type="number"
                                        value={formData.age || ""}
                                        onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value ? parseInt(e.target.value, 10) : null }))}
                                        style={{ marginTop: 4, fontWeight: 600 }}
                                        min="0"
                                        placeholder="Enter age"
                                    />
                                </div>
                                <div style={{ display: "flex", gap: 16, marginTop: 20 }}>
                                    <Button
                                        type="primary"
                                        icon={<CheckOutlined />}
                                        loading={localLoading}
                                        onClick={handleSave}
                                        style={{ fontWeight: 600 }}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        icon={<CloseOutlined />}
                                        onClick={() => {
                                            setEditMode(false);
                                            setFormData({
                                                firstName: userData.firstName || "",
                                                lastName: userData.lastName || "",
                                                email: userData.email || "",
                                                mobileNo: userData.mobileNo || "",
                                                dob: userData.dob || "",
                                                age: userData.age || "",
                                            });
                                        }}
                                        style={{ fontWeight: 600 }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Space>
                        ) : (
                            <>
                                <div className="pro-info-item">
                                    <span className="info-label">First Name:</span>
                                    <span className="info-value">{userData.firstName || "Not set"}</span>
                                </div>
                                <div className="pro-info-item">
                                    <span className="info-label">Last Name:</span>
                                    <span className="info-value">{userData.lastName || "Not set"}</span>
                                </div>
                                <div className="pro-info-item">
                                    <span className="info-label">Email:</span>
                                    <span className="info-value">{userData.email || "user@simplyfi.co.in"}</span>
                                </div>
                                <div className="pro-info-item">
                                    <span className="info-label">Phone:</span>
                                    <span className="info-value">{userData.mobileNo || "Not set"}</span>
                                </div>
                                <div className="pro-info-item">
                                    <span className="info-label">Age:</span>
                                    <span className="info-value">{userData.age ? `${userData.age}` : "Not set"}</span>
                                </div>
                                <div className="pro-info-item">
                                    <span className="info-label">Date of Birth:</span>
                                    <span className="info-value">{userData.dob ? formatDate(userData.dob) : "Not set"}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {/* Security */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3>Security</h3>
                    </div>
                    <div className="card-content">
                        <div className="security-item">
                            <span className="security-label">2FA Status:</span>
                            <button className="manage-btn" onClick={handleManage2FA}>
                                Manage 2FA
                            </button>
                        </div>
                        <div className="security-item">
                            <span className="security-label">Change Password:</span>
                            <button className="change-password-btn" onClick={handleChangePassword}>
                                Change Password
                            </button>
                        </div>
                        <div className="account-info">
                            <h4>Account Information</h4>
                            <div className="account-details">
                                <p>User ID: {userData.id}</p>
                                <p>Last Password Change: {userData.lastPasswordChange ? formatDateTime(userData.lastPasswordChange) : "Never"}</p>
                                <p>Account Created: {formatDateTime(userData.createdAt)}</p>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Preferences */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3>Preferences</h3>
                    </div>
                    <div className="card-content">
                        <div className="preference-item">
                            <span className="preference-label">Dark Mode</span>
                            <div
                                className={`toggle-switch ${isDark ? 'active' : ''}`}
                                onClick={toggleTheme}
                            >
                                <div className="toggle-slider"></div>
                            </div>
                        </div>
                        <div className="preference-item">
                            <span className="preference-label">Notifications</span>
                            <div
                                className={`toggle-switch ${notifEnabled ? 'active' : ''}`}
                                onClick={() => setNotifEnabled(!notifEnabled)}
                            >
                                <div className="toggle-slider"></div>
                            </div>
                        </div>
                        <div className="account-actions">
                            <h4>Account Actions</h4>
                            <div className="action-buttons">
                                <button className="logout-btn" onClick={handleLogout}>
                                    Logout
                                </button>
                                <button className="delete-btn" onClick={() => setDeleteModal(true)}>
                                    Delete Account
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2FA Methods Modal */}
            <Modal
                open={twoFAModal}
                onCancel={() => setTwoFAModal(false)}
                footer={null}
                title="Two-Factor Authentication Methods"
                width={600}
                centered
            >
                <div style={{ marginBottom: 24 }}>
                    <Text style={{ fontSize: 16, color: "#666" }}>
                        Select a 2FA method to enable or manage your existing methods:
                    </Text>
                </div>

                <List
                    dataSource={availableMethods}
                    renderItem={(method) => (
                        <List.Item
                            style={{
                                padding: "16px",
                                border: "1px solid #f0f0f0",
                                borderRadius: "8px",
                                marginBottom: "12px",
                                background: method.enabled ? "#f6ffed" : "#fff",
                            }}
                            actions={
                                method.enabled
                                    ? [
                                        <Button
                                            key="disable"
                                            danger
                                            size="small"
                                            loading={twoFALoading}
                                            onClick={() => handleDisableMethod(method.code, method.name)}
                                        >
                                            Disable
                                        </Button>
                                    ]
                                    : method.setupCompleted
                                        ? [
                                            <Button
                                                key="enable"
                                                type="primary"
                                                size="small"
                                                onClick={() => {
                                                    setEnable2FAMethod(method.code);
                                                    setEnable2FAModal(true);
                                                }}
                                            >
                                                Enable
                                            </Button>
                                        ]
                                        : [
                                            <Button
                                                key="setup"
                                                type="primary"
                                                size="small"
                                                onClick={() => {
                                                    setSelectedMethod(method.code);
                                                    setTwoFAModal(false);
                                                    // Navigate to 2FA setup page
                                                    const isUserAdmin = isAdmin();
                                                    if (isUserAdmin) {
                                                        navigate("/admin/2fa");
                                                    } else {
                                                        navigate("/d/2fa");
                                                    }
                                                }}
                                            >
                                                Setup
                                            </Button>
                                        ]
                            }
                        >
                            <List.Item.Meta
                                avatar={getMethodIcon(method.code)}
                                title={
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontWeight: 600 }}>
                                            {method.name}
                                        </span>
                                        {method.enabled && (
                                            <span
                                                style={{
                                                    background: "#52c41a",
                                                    color: "white",
                                                    padding: "2px 8px",
                                                    borderRadius: "12px",
                                                    fontSize: "12px",
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Active
                                            </span>
                                        )}
                                    </div>
                                }
                                description={
                                    <div>
                                        <Text style={{ color: "#666", fontSize: 14 }}>
                                            {method.description || `Use ${method.name} for two-factor authentication`}
                                        </Text>
                                        {method.enabled && (
                                            <div style={{ marginTop: 8 }}>
                                                <Text style={{ fontSize: 12, color: "#52c41a" }}>
                                                    ✓ Enabled and setup completed
                                                </Text>
                                            </div>
                                        )}
                                        {!method.enabled && method.setupCompleted && (
                                            <div style={{ marginTop: 8 }}>
                                                <Text style={{ fontSize: 12, color: "#faad14" }}>
                                                    ⚠ Setup completed, needs to be enabled
                                                </Text>
                                            </div>
                                        )}
                                        {!method.enabled && !method.setupCompleted && (
                                            <div style={{ marginTop: 8 }}>
                                                <Text style={{ fontSize: 12, color: "#666" }}>
                                                    Setup required
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                        </List.Item>
                    )}
                />

                {availableMethods.some(method => method.enabled) && (
                    <div style={{ marginTop: 24, padding: "16px", background: "#fff2e8", borderRadius: "8px", border: "1px solid #ffd591" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                                <Text style={{ fontWeight: 600, color: "#d46b08" }}>
                                    Disable All 2FA Methods
                                </Text>
                                <div>
                                    <Text style={{ fontSize: 14, color: "#666" }}>
                                        This will disable all active 2FA methods and make your account less secure.
                                    </Text>
                                </div>
                            </div>
                            <Button
                                danger
                                loading={twoFALoading}
                                onClick={() => {
                                    Modal.confirm({
                                        title: 'Disable All 2FA Methods',
                                        content: 'Are you sure you want to disable all 2FA methods? This will make your account less secure and cannot be undone.',
                                        okText: 'Disable All',
                                        okType: 'danger',
                                        cancelText: 'Cancel',
                                        onOk: handleDisableAll2FA,
                                    });
                                }}
                            >
                                Disable All
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Enable 2FA Modal */}
            <Modal
                open={enable2FAModal}
                onCancel={() => {
                    setEnable2FAModal(false);
                    setEnable2FACode("");
                    setEnable2FAMethod(null);
                }}
                onOk={handleEnable2FA}
                okText="Enable"
                okButtonProps={{ loading: enable2FALoading }}
                title={`Enable ${enable2FAMethod ? availableMethods.find(m => m.code === enable2FAMethod)?.name : ''} 2FA`}
            >
                <div style={{ padding: "16px 0" }}>
                    <Text style={{ marginBottom: 16, display: "block" }}>
                        Enter the verification code to enable this 2FA method:
                    </Text>
                    <Input
                        placeholder="Enter 6-digit code"
                        value={enable2FACode}
                        onChange={(e) => setEnable2FACode(e.target.value)}
                        maxLength={6}
                        style={{ marginBottom: 16 }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Enter the code from your authenticator app or the code sent to your email/phone.
                    </Text>
                </div>
            </Modal>

            {/* Subscription Details Section */}
            <div className="subscription-section">
                <SubscriptionDetails />
            </div>

            {/* Delete Account Modal */}
            <Modal
                open={deleteModal}
                onCancel={() => setDeleteModal(false)}
                onOk={handleDeleteAccount}
                okText="Delete"
                okButtonProps={{ danger: true }}
                title="Delete Account"
            >
                <p>
                    Are you sure you want to delete your account? This action cannot be
                    undone.
                </p>
            </Modal>
        </div >
    );
};

export default Profile;
